import React, { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import LandingNav from './LandingNav';
import LandingFooter from './LandingFooter';
import HeroAccumulation from './HeroAccumulation';
import EaseStepperSection from './EaseStepperSection';
import TwoWaysSection from './TwoWaysSection';
import SeoMeta from '../SeoMeta';
import { track } from '../../services/analytics';
import { PRIMARY_DEMO_ACCOUNT_ID } from '../../config/demoAccounts';
import './LandingPage.css';

/* Trades campaign landing page (/for/trades).
   Mirrors the default landing page but keeps the van-flavoured voice: the hero
   speaks to sole traders "on the road", and the stepper section uses
   EaseStepperSection's default heading ("Added from the van. Live before you
   drive off.") — that heading was moved off the main page to live here.

   Scroll-reveal hook is duplicated (as on the other landing pages) since the
   pages diverge in section count over time. */
function useScrollReveal() {
  const refs = useRef<(HTMLElement | null)[]>([]);

  const setRef = useCallback((index: number) => (el: HTMLElement | null) => {
    refs.current[index] = el;
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      refs.current.forEach(el => el?.classList.add('landing-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('landing-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    refs.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return setRef;
}

const TradesLandingPage: React.FC = () => {
  const setRef = useScrollReveal();

  useEffect(() => {
    track('landing_view', { pathname: '/for/trades' });
  }, []);

  return (
    <div className="landing-page">
      <SeoMeta
        title="Map of Projects for Trades — Every job on a map, from the van"
        description="Add finished jobs to a live map straight from the van, in about a minute. Embed it on your site or share the link — no design or dev skills needed."
        path="/for/trades"
      />
      <a href="#main-content" className="landing-skip-link">Skip to content</a>
      <LandingNav />

      {/* ===== 1. Hero ===== */}
      <main id="main-content">
      <section className="landing-hero">
        <HeroAccumulation />
        <Container>
          <span className="landing-hero__eyebrow">For trades on the road</span>
          <h1 className="landing-hero__title">
            Put your work <em>on the map</em>.
          </h1>
          <p className="landing-hero__subtitle">
            Pin every finished job to a live map — straight from the van, in about a minute. Embed it on your site, or share it as its own public page.
          </p>
          <div className="landing-hero__actions">
            <Link to="/login" className="landing-btn-primary">
              Start free
            </Link>
            <Link to={`/${PRIMARY_DEMO_ACCOUNT_ID}`} className="landing-btn-secondary">
              See a live example
            </Link>
          </div>
          <div className="landing-hero__trust">
            No credit card. No developer. Set up in under five minutes.
          </div>
        </Container>
      </section>

      {/* ===== 2. Added from the van (interactive stepper — default heading) ===== */}
      <EaseStepperSection band="light" revealRef={setRef(0)} />

      {/* ===== 3. One map, two ways to show it ===== */}
      <TwoWaysSection band="white" revealRef={setRef(11)} />

      {/* ===== 4. Pin Preview ===== */}
      <section className="landing-section landing-section--light" ref={setRef(6)}>
        <Container>
          <div className="landing-feature landing-reveal" ref={setRef(7)}>
            <div className="landing-feature__text">
              <span className="landing-feature__eyebrow">Pin previews</span>
              <h3>Preview photos on every pin</h3>
              <p>
                Every pin shows a thumbnail preview on hover, so visitors get the visual story before they open the full write-up. The kind of signal clients act on.
              </p>
            </div>
            <div className="landing-feature__image">
              <img
                src="/images/landing/pin-preview.jpg"
                alt="Map pin with photo preview on hover"
                loading="lazy"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* ===== 5. Case Study Page ===== */}
      <section className="landing-section" ref={setRef(8)}>
        <Container>
          <div className="landing-feature landing-feature--reverse landing-reveal" ref={setRef(9)}>
            <div className="landing-feature__image">
              <img
                src="/images/landing/case-study-screenshot.png"
                alt="Detailed page for a single location"
                loading="lazy"
              />
            </div>
            <div className="landing-feature__text">
              <span className="landing-feature__eyebrow">Detail pages</span>
              <h3>A dedicated page behind every pin</h3>
              <p>
                Photo gallery, description, location, and context. Or paste a link to a write-up you already have on your own site and the pin opens it directly. Either way, prospects see what the job actually looked like.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ===== 6. Market Trend Callout ===== */}
      <section className="landing-section landing-section--light">
        <Container>
          <div className="landing-quotes landing-reveal" ref={setRef(1)}>
            <div className="landing-quote">
              <p className="landing-quote__text">
                &ldquo;90% of buyers report that social proof - customer stories, case studies, and visual evidence of past work - heavily influences their shortlist decisions.&rdquo;
              </p>
              <span className="landing-quote__source">Gartner Digital Markets, 2025</span>
            </div>
            <div className="landing-quote">
              <p className="landing-quote__text">
                &ldquo;87% of B2B marketers agree that interactive content captures attention more effectively than static content.&rdquo;
              </p>
              <span className="landing-quote__source">Content Marketing Institute</span>
            </div>
          </div>
        </Container>
      </section>

      {/* ===== 7. Pricing / CTA ===== */}
      <section className="landing-section landing-section--dark" ref={setRef(16)}>
        <Container>
          <div className="landing-pricing landing-reveal" ref={setRef(17)}>
            <span className="landing-section__eyebrow">Pricing</span>
            <h2 className="landing-section__title">Start free. Upgrade when you&rsquo;re ready.</h2>
            <p className="landing-section__subtitle">
              No credit card required. Live map in minutes.
            </p>

            <div className="landing-pricing__card">
              <div className="landing-pricing__free-label">Free plan</div>
              <ul className="landing-pricing__features">
                <li>Up to 10 entries</li>
                <li>3 photos per entry</li>
                <li>Interactive Google Maps integration</li>
                <li>Shareable link and embed snippet</li>
              </ul>
            </div>

            <div>
              <Link to="/login" className="landing-btn-primary">
                Start free
              </Link>
            </div>
          </div>
        </Container>
      </section>
      </main>

      {/* ===== 8. Footer ===== */}
      <LandingFooter />
    </div>
  );
};

export default TradesLandingPage;
